import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";

import { DetailField, FieldGrid } from "~/components/detail-field";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { errorMessage, isNotFound } from "~/lib/api";
import { formatDate, orDash } from "~/lib/format";
import { useMerchant, type MerchantDetail } from "~/lib/queries";
import { FeeSchedulesTab } from "./-fee-schedules";
import { StatementsTab } from "./-statements";

export const Route = createFileRoute("/_authed/merchants/$merchantId")({
  component: MerchantDetailPage,
});

/**
 * One merchant: who they are, which shops are theirs, what Kugie charges
 * them, and the settlement statements and payouts that pay them (PRO-1400).
 * Fee schedules open first, because a merchant without one cannot be settled
 * and that is the reason most people land here.
 *
 * Read-only for the merchant record itself; the legal name, NPWP and bank
 * details are edited through PUT /admin/merchants/:id until this screen grows
 * a form for them.
 */
function MerchantDetailPage() {
  const { merchantId } = Route.useParams();
  const { data, error, isPending, refetch } = useMerchant(merchantId);

  if (isPending) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {isNotFound(error) ? "No such merchant" : "Couldn't load this merchant"}
            </CardTitle>
            <CardDescription>{isNotFound(error) ? `No merchant has the id ${merchantId}.` : errorMessage(error)}</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2">
            {!isNotFound(error) ? (
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" render={<Link to="/merchants" />}>
              Back to merchants
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/merchants" />}>
          <ArrowLeftIcon className="size-3.5" />
          All merchants
        </Button>
      </div>

      <PageHeader title={data.name} description={<StatusBadge status={data.status} />} />

      <Overview merchant={data} />

      <Tabs defaultValue="fees">
        <TabsList>
          <TabsTrigger value="fees">Fee schedules</TabsTrigger>
          <TabsTrigger value="statements">Statements &amp; payouts</TabsTrigger>
          <TabsTrigger value="storefronts">Storefronts ({data.storefronts.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="fees" className="pt-4">
          <FeeSchedulesTab merchantId={merchantId} merchantName={data.name} />
        </TabsContent>
        <TabsContent value="statements" className="pt-4">
          <StatementsTab merchant={data} />
        </TabsContent>
        <TabsContent value="storefronts" className="pt-4">
          <Storefronts merchant={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Overview({ merchant }: { merchant: MerchantDetail }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <FieldGrid>
          <DetailField label="NPWP" copy={merchant.npwp} mono>
            {orDash(merchant.npwp)}
          </DetailField>
          <DetailField label="Bank account">
            {merchant.has_bank_account
              ? `${orDash(merchant.bank_code)} ••••${merchant.bank_account_last4 ?? ""} · ${orDash(merchant.bank_account_holder)}`
              : "not set (needed before the first payout)"}
          </DetailField>
          <DetailField label="Address">{orDash(merchant.address)}</DetailField>
          <DetailField label="Merchant ID" copy={merchant.merchant_id} mono>
            {merchant.merchant_id}
          </DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

function Storefronts({ merchant }: { merchant: MerchantDetail }) {
  if (merchant.storefronts.length === 0) {
    return <p className="text-sm text-muted-foreground">No storefronts belong to this merchant.</p>;
  }
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Name</TableHead>
            <TableHead>Shop domain</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {merchant.storefronts.map((storefront) => (
            <TableRow key={storefront.storefront_id}>
              <TableCell className="font-medium">
                <Link
                  to="/storefronts/$storefrontId"
                  params={{ storefrontId: storefront.storefront_id }}
                  className="text-primary hover:underline"
                >
                  {storefront.storefront_name}
                </Link>
              </TableCell>
              <TableCell className="text-muted-foreground">{orDash(storefront.shop_domain)}</TableCell>
              <TableCell className="text-muted-foreground">{formatDate(storefront.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
